import React, { useMemo, useState } from 'react';
import { addWeeks, format, getISOWeek, parseISO, startOfWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarRange, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

/**
 * Projektkalender je Kostenstelle, wochenweise.
 *
 * Warum Wochen und keine Tage: Ein Dachprojekt läuft Wochen bis Monate. Auf
 * Tagesspalten bräuchte ein halbes Jahr 180 Spalten – man scrollt und sieht
 * nie zwei Projekte nebeneinander. In Kalenderwochen passt ein halbes Jahr auf
 * einen Blick, und im Betrieb wird ohnehin in KW gesprochen.
 *
 * Angezeigt wird pro Kostenstelle eine Zeile je Projekt mit einem Balken von
 * Start- bis Endwoche. Projekte ohne Überschneidung mit dem Zeitraum fallen
 * weg, sonst stehen lauter leere Zeilen da.
 */

const ZEITRAEUME = [
  { key: '13', label: '3 Monate', wochen: 13 },
  { key: '26', label: '6 Monate', wochen: 26 },
  { key: '52', label: '1 Jahr', wochen: 52 },
];

const STATUS_FARBE = {
  Aktiv: 1,
  Pausiert: 0.45,
  Abgeschlossen: 0.3,
};

function montagDerWoche(d) {
  return startOfWeek(d, { weekStartsOn: 1 });
}

export default function ProjektKalender({ projects = [], costCenters = [] }) {
  const [zeitraum, setZeitraum] = useState('26');
  const wochenAnzahl = (ZEITRAEUME.find(z => z.key === zeitraum) || ZEITRAEUME[1]).wochen;

  // Verschiebung des Betrachtungsfensters in Wochen. 0 = Standardlage, also
  // zwei Wochen vor der laufenden Woche – was gerade ausläuft, ist für die
  // Planung genauso wichtig wie das Kommende.
  //
  // Geblättert wird um die halbe Fensterbreite, nicht um eine Woche: Bei einem
  // Halbjahresfenster wäre Wochenweise-Blättern sinnlos viel Klicken, und ein
  // ganzes Fenster weiter verliert den Zusammenhang, weil kein Balken mehr zu
  // sehen ist, den man vorher hatte.
  const [verschiebung, setVerschiebung] = useState(0);
  const schrittweite = Math.max(1, Math.round(wochenAnzahl / 2));

  const wochen = useMemo(() => {
    const start = addWeeks(montagDerWoche(new Date()), -2 + verschiebung);
    return Array.from({ length: wochenAnzahl }, (_, i) => {
      const montag = addWeeks(start, i);
      return { montag, kw: getISOWeek(montag) };
    });
  }, [wochenAnzahl, verschiebung]);

  const ersterMontag = wochen[0].montag;
  const letzterMontag = wochen[wochen.length - 1].montag;

  /** In welche Spalte fällt ein Datum? Kann vor oder hinter dem Raster liegen. */
  const spalteVon = (iso) => {
    if (!iso) return null;
    try {
      const montag = montagDerWoche(parseISO(iso));
      return Math.round((montag - ersterMontag) / (7 * 86400000));
    } catch { return null; }
  };

  // Jede Kostenstelle bekommt einen Abschnitt – auch eine ohne Projekte im
  // Zeitraum. Vorher fielen leere weg; dann fehlt etwa Schabos komplett und
  // man weiß nicht, ob dort nichts läuft oder ob die Anzeige etwas verschluckt.
  // "Nichts da" ist eine Auskunft, Abwesenheit ist keine.
  const proKostenstelle = useMemo(() => {
    const sichtbar = projects.filter(p => p.status !== 'Archiviert');
    return costCenters
      .filter(k => !k.deleted && k.active)
      .map(kst => {
        const zeilen = [];
        for (const p of sichtbar) {
          if (p.cost_center_id !== kst.id) continue;
          const von = spalteVon(p.start_date);
          const bis = spalteVon(p.current_end_date || p.original_end_date || p.start_date);
          if (von === null || bis === null) continue;
          // Überschneidet das Projekt den gezeigten Zeitraum überhaupt?
          if (bis < 0 || von > wochen.length - 1) continue;
          // Angeschnitten: Balken an der Rasterkante abschneiden und das
          // kennzeichnen, damit niemand denkt, dort sei das Projektende.
          const startSpalte = Math.max(0, von);
          const endSpalte = Math.min(wochen.length - 1, bis);
          zeilen.push({
            projekt: p,
            startSpalte,
            spannweite: endSpalte - startSpalte + 1,
            vorherBegonnen: von < 0,
            danachEnde: bis > wochen.length - 1,
          });
        }
        zeilen.sort((a, b) => {
          if (a.startSpalte !== b.startSpalte) return a.startSpalte - b.startSpalte;
          const an = a.projekt.project_number || a.projekt.name || '';
          const bn = b.projekt.project_number || b.projekt.name || '';
          return an.localeCompare(bn);
        });
        return { kst, zeilen };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, costCenters, wochen]);

  const rasterSpalten = `minmax(170px, 1fr) repeat(${wochen.length}, minmax(26px, 1fr))`;

  return (
    <Card data-testid="projekt-kalender">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <CalendarRange size={18} />Projektzeiträume je Kostenstelle
          </CardTitle>
          <div className="flex items-center gap-1.5">
            {/* Der Zeitraum steht zwischen den Pfeilen und nicht als Satz
                darunter: Beim Blättern schaut man dorthin, wo man geklickt
                hat. */}
            <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                    onClick={() => setVerschiebung(v => v - schrittweite)}
                    title={`${schrittweite} Wochen zurück`}
                    data-testid="kalender-zurueck">
              <ChevronLeft size={15} />
            </Button>
            <span className="text-xs font-mono font-semibold text-slate-700 px-1 whitespace-nowrap">
              {format(ersterMontag, 'dd.MM.yy', { locale: de })} – {format(addWeeks(letzterMontag, 1), 'dd.MM.yy', { locale: de })}
            </span>
            <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                    onClick={() => setVerschiebung(v => v + schrittweite)}
                    title={`${schrittweite} Wochen vor`}
                    data-testid="kalender-vor">
              <ChevronRight size={15} />
            </Button>
            {verschiebung !== 0 && (
              <Button size="sm" variant="outline" className="h-8 px-2 text-xs"
                      onClick={() => setVerschiebung(0)} data-testid="kalender-heute">
                Heute
              </Button>
            )}
            <Select value={zeitraum} onValueChange={setZeitraum}>
              <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="kalender-zeitraum">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ZEITRAEUME.map(z => <SelectItem key={z.key} value={z.key}>{z.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>

        <div className="overflow-x-auto scroll-thin">
          <div className="min-w-[760px] space-y-4">
            {proKostenstelle.map(({ kst, zeilen }) => (
              <div key={kst.id} data-testid={`kalender-kst-${kst.short_code}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: kst.color }} />
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                    {kst.short_code} – {kst.name}
                  </span>
                  <span className="text-[11px] text-slate-400 font-mono">{zeilen.length}</span>
                </div>

                {/* Kopfzeile mit den Kalenderwochen */}
                <div className="grid items-center" style={{ gridTemplateColumns: rasterSpalten }}>
                  <div />
                  {wochen.map((w, i) => (
                    <div key={i}
                         className="text-[9px] font-mono text-center text-slate-500 border-l border-slate-100"
                         title={format(w.montag, "'Woche ab' dd.MM.yyyy", { locale: de })}>
                      {w.kw}
                    </div>
                  ))}
                </div>

                {zeilen.length === 0 && (
                  <div className="text-xs text-slate-400 italic py-1 pl-1">
                    Keine Projekte in diesem Zeitraum.
                  </div>
                )}

                {zeilen.map(z => (
                  <div key={z.projekt.id}
                       className="grid items-center py-0.5 hover:bg-slate-50 rounded"
                       style={{ gridTemplateColumns: rasterSpalten }}
                       data-testid={`kalender-projekt-${z.projekt.id}`}>
                    <div className="pr-2 min-w-0">
                      <div className="text-[11px] font-semibold text-slate-800 truncate"
                           title={`${z.projekt.project_number || ''} ${z.projekt.name}`}>
                        {z.projekt.project_number ? `${z.projekt.project_number} · ` : ''}{z.projekt.name}
                      </div>
                      <div className="text-[9px] font-mono text-slate-500">
                        {z.projekt.start_date ? format(parseISO(z.projekt.start_date), 'dd.MM.yy') : '—'}
                        {' – '}
                        {(z.projekt.current_end_date || z.projekt.original_end_date)
                          ? format(parseISO(z.projekt.current_end_date || z.projekt.original_end_date), 'dd.MM.yy')
                          : '—'}
                      </div>
                    </div>
                    {/* Der Balken sitzt im Wochenraster: Spalte 2 ist die erste
                        Woche, deshalb +2. */}
                    <div
                      className="h-4 rounded-sm flex items-center justify-between px-1 text-[8px] font-bold text-white"
                      style={{
                        gridColumn: `${z.startSpalte + 2} / span ${z.spannweite}`,
                        background: kst.color,
                        opacity: STATUS_FARBE[z.projekt.status] ?? 1,
                      }}
                      title={`${z.projekt.name} · ${z.projekt.status}`}
                    >
                      <span>{z.vorherBegonnen ? '«' : ''}</span>
                      <span>{z.danachEnde ? '»' : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-3 text-[10px] text-slate-500">
          <span className="flex items-center gap-1">
            <span className="w-4 h-2.5 rounded-sm bg-slate-700" />Aktiv
          </span>
          <span className="flex items-center gap-1">
            <span className="w-4 h-2.5 rounded-sm bg-slate-700 opacity-45" />Pausiert
          </span>
          <span className="flex items-center gap-1">
            <span className="w-4 h-2.5 rounded-sm bg-slate-700 opacity-30" />Abgeschlossen
          </span>
          <span>« bzw. » = Balken reicht über den gezeigten Zeitraum hinaus.</span>
          <span>Archivierte Projekte werden nicht gezeigt.</span>
        </div>
      </CardContent>
    </Card>
  );
}
